import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { service } from '@ember/service';
import { LinkTo } from '@ember/routing';
import { VStack, Section } from 'tio-ui/components/layout';
import Heading from 'tio-reimagined/components/heading';
import Text from 'tio-reimagined/components/text';
import { eq } from 'tio-ui/utilities';
import { formatISODateToDisplay } from 'tio-common/utils/date';
import formatCurrencySuperscriptCents from 'tio-common/helpers/format-currency-superscript-cents';
import { htmlSafe } from '@ember/template';
import Chip from 'tio-reimagined/components/chip';
import Button from 'tio-reimagined/components/button';
import { t } from 'ember-intl';
import type { IntlService } from 'ember-intl';
import {
  PhArrowRight,
  PhShieldCheck,
  PhBookOpen,
  PhGraduationCap,
  PhLock,
  PhBank,
  PhCompass,
} from 'ember-phosphor-icons';
import PageHero from 'tio-reimagined/components/page-hero';
import ActivityCard from 'tio-employee/components/reimagined/tas/activity-card';
import DataIndicator from 'tio-employee/components/reimagined/data-indicator';
import type { DataSource } from 'tio-employee/components/reimagined/data-indicator';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import type SessionContextService from 'tio-employee/services/session-context';
import {
  isActiveInstanceState,
  isActiveApplicationState,
} from 'tio-common/utils/tuition-assistance/application-states';
import type {
  DataSourceInfo,
  EligibleTemplate,
  LiveTasApplication,
  LiveTasData,
  LiveTasProgram,
} from 'tio-employee/services/reimagined-data';
import type { LimitsStatusDataSignature } from 'tio-common/services/tuition-assistance';
import pageTitle from 'ember-page-title/helpers/page-title';

interface TasDashboardModel {
  tasData: LiveTasData;
  tasUsedAmount: { yearToDate: number; isLiveData: boolean };
  dataSourceInfo: DataSourceInfo;
  eligibleTemplates: EligibleTemplate[];
  limitsStatus?: LimitsStatusDataSignature;
}

const PAID_APPLICATION_STATES = new Set([
  'TAS.ApplicationPaymentState.PAID',
  'TAS.ApplicationPaymentState.PAID_IN_FULL',
]);

const PENDING_APPLICATION_STATES = new Set([
  'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
  'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
]);

const REQUESTED_APPLICATION_STATES = new Set([
  'TAS.ApplicationState.CREATED',
  'TAS.ApplicationState.DEFINE_COURSES',
]);

/** Terminal negative states — hide after 1 month */
const STALE_FILTERABLE_STATES = new Set([
  'TAS.ApplicationState.COURSES_REJECTED',
  'TAS.ApplicationState.EVIDENCE_REJECTED',
  'TAS.ApplicationState.REJECTED_PER_POLICY',
  'TAS.ApplicationState.ABANDONED',
]);

/** Payment states that indicate the application has been paid */
const PAID_PAYMENT_STATES = new Set([
  'TAS.ApplicationPaymentState.PAID',
  'TAS.ApplicationPaymentState.PAID_IN_FULL',
  'TAS.ApplicationPaymentState.PAID_IN_PART',
]);

/** Payment state indicating active repayment — always shown */
const REPAYMENT_PAYMENT_STATE = 'TAS.ApplicationPaymentState.REFUND_REQUESTED';

/** Program instance states where the employee cannot yet add requests */
const PENDING_PROGRAM_STATES = new Set([
  'TAS.ProgramInstanceState.CREATED',
  'TAS.ProgramInstanceState.DRAFT',
  'TAS.ProgramInstanceState.PENDING_PROGRAM_APPROVAL',
]);

function canAddRequests(state: string) {
  return !PENDING_PROGRAM_STATES.has(state);
}

/** Type 4 programs — only these show as standalone program cards in Activity */
const TYPE_4_CLASSIFICATIONS = new Set(['TAS.ProgramType.4.2', 'TAS.ProgramType.4.3']);

export interface S {
  Args: {
    model: TasDashboardModel;
  };
}

type ActivityItem =
  | { kind: 'application'; data: LiveTasApplication; sortDate: string }
  | { kind: 'program'; data: LiveTasProgram; sortDate: string };

export default class TasDashboard extends Component<S> {
  @service declare reimaginedData: ReimaginedDataService;
  @service declare intl: IntlService;
  @service declare sessionContext: SessionContextService;

  get greetingText() {
    const hour = new Date().getHours();
    let base: string;
    if (hour < 12) {
      base = this.intl.t('tuition_assistance.reimagined.dashboard.greeting_morning');
    } else if (hour < 17) {
      base = this.intl.t('tuition_assistance.reimagined.dashboard.greeting_afternoon');
    } else {
      base = this.intl.t('tuition_assistance.reimagined.dashboard.greeting_evening');
    }
    const name = this.firstName;
    return name ? `${base}, ${name}` : base;
  }

  get firstName() {
    return this.sessionContext.currentEmployee?.firstName || '';
  }

  get isDemoMode() {
    return this.reimaginedData.isDemoMode;
  }

  get isDependentProgramsEnabled() {
    return (
      this.sessionContext.currentEmployee?.company.companySetting.tuitionAssistance
        ?.isDependentProgramsEnabled || false
    );
  }

  get isEligible() {
    return this.args.model.tasData.isEligible;
  }

  get tasSource(): DataSource {
    if (this.isDemoMode) return 'demo';
    return this.args.model.tasData.isLiveData ? 'live' : 'placeholder';
  }

  get programs(): LiveTasProgram[] {
    return this.args.model.tasData.programs.filter(
      (p) =>
        isActiveInstanceState(p.state) &&
        p.typeClassification !== null &&
        TYPE_4_CLASSIFICATIONS.has(p.typeClassification)
    );
  }

  get applications(): LiveTasApplication[] {
    return this.args.model.tasData.applications.filter((app) => app.recordType === 'application');
  }

  get totalApproved() {
    return this.args.model.tasData.summary.totalApproved;
  }

  get totalPaid() {
    return this.args.model.tasData.summary.totalPaid;
  }

  get eligibleTemplates(): EligibleTemplate[] {
    return this.args.model.eligibleTemplates ?? [];
  }

  /**
   * Count of applications awaiting payment (approved but not yet paid)
   */
  get awaitingPayment() {
    return this.totalApproved - this.totalPaid;
  }

  /**
   * Count of applications in review (PENDING_COURSES_APPROVAL or PENDING_EVIDENCE_APPROVAL)
   */
  get inReviewCount() {
    return this.applications.filter((app) => PENDING_APPLICATION_STATES.has(app.state)).length;
  }

  /**
   * Correctly count active items based on program type:
   * - Type 2, 3: Count only active applications (ignore program instance)
   * - Type 1, 4.2, 4.3: Count active instances + active applications
   * - Active instances with no child applications (e.g. Draft) are also counted
   */
  @cached
  get properActiveCount() {
    // Pre-group applications by programInstanceId to avoid repeated full scans
    const appsByProgramInstance = new Map<string, LiveTasApplication[]>();
    const activeApplications = this.args.model.tasData.applications.filter(
      (app) => app.recordType === 'application' && isActiveApplicationState(app.state)
    );

    for (const app of activeApplications) {
      if (!app.programInstanceId) continue;
      let list = appsByProgramInstance.get(app.programInstanceId);
      if (!list) {
        list = [];
        appsByProgramInstance.set(app.programInstanceId, list);
      }
      list.push(app);
    }

    // Build a map of programTemplateId -> typeClassification from programs
    const templateTypeMap = new Map<string, string>();

    for (const program of this.args.model.tasData.programs) {
      if (program.typeClassification && isActiveInstanceState(program.state)) {
        const allAppsByInstance = appsByProgramInstance.get(program.id) ?? [];
        for (const app of allAppsByInstance) {
          if (app.programTemplateId) {
            templateTypeMap.set(app.programTemplateId, program.typeClassification);
          }
        }
      }
    }

    let count = 0;

    // Group applications by template type
    const type2or3Apps: LiveTasApplication[] = [];
    const type1or4Apps: LiveTasApplication[] = [];
    const unknownTypeApps: LiveTasApplication[] = [];

    for (const app of activeApplications) {
      const templateId = app.programTemplateId;
      if (!templateId) {
        continue;
      }

      const typeClassification = templateTypeMap.get(templateId);
      if (
        typeClassification === 'TAS.ProgramType.2' ||
        typeClassification === 'TAS.ProgramType.3'
      ) {
        type2or3Apps.push(app);
      } else if (
        typeClassification === 'TAS.ProgramType.1' ||
        typeClassification === 'TAS.ProgramType.4.2' ||
        typeClassification === 'TAS.ProgramType.4.3'
      ) {
        type1or4Apps.push(app);
      } else {
        unknownTypeApps.push(app);
      }
    }

    // Type 2/3: Count only active applications
    count += type2or3Apps.length;

    // Type 1/4.2/4.3: Count active instances + active applications
    const instancesWithApps = new Set(
      type1or4Apps.map((app) => app.programInstanceId).filter((id): id is string => id !== null)
    );
    count += instancesWithApps.size; // Count unique instances that have apps
    count += type1or4Apps.length; // Count applications

    // Count active program instances that have NO child applications (e.g. Draft instances)
    // These are instances in active states where the employee hasn't started an application yet
    const instancesWithAnyApps = new Set(
      this.args.model.tasData.applications
        .filter((app) => app.recordType === 'application' && app.programInstanceId)
        .map((app) => app.programInstanceId)
    );
    const standaloneActiveInstances = this.args.model.tasData.programs.filter(
      (p) => isActiveInstanceState(p.state) && !instancesWithAnyApps.has(p.id)
    );
    count += standaloneActiveInstances.length;

    // Fallback for unknown types: count applications only
    count += unknownTypeApps.length;

    return count;
  }

  /**
   * Count of completed applications
   */
  get completedCount() {
    return this.args.model.tasData.summary.completedCount;
  }

  /**
   * Whether there are any active programs or applications
   * Used to determine which hero layout to show.
   * Checks ALL active program types (not just Type 4) so that Type 1
   * instances also inform the hero display.
   */
  get hasActiveActivity() {
    const hasActivePrograms = this.args.model.tasData.programs.some((p) =>
      isActiveInstanceState(p.state)
    );
    return hasActivePrograms || this.applications.length > 0;
  }

  /**
   * Unified activity list merging applications + standalone program instances.
   * Sorted by updatedAt descending (most recent first).
   *
   * Visibility rules:
   * - In repayment (REFUND_REQUESTED) → always shown
   * - Rejected / abandoned → shown only if last action < 1 month
   * - Fulfilled + paid → shown only if last action < 1 month
   * - Fulfilled + not paid → always shown (still active)
   * - All other states → always shown
   */
  get activityItems() {
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

    const visibleApps = this.applications.filter((app) => {
      // In repayment → always show
      if (app.paymentState === REPAYMENT_PAYMENT_STATE) return true;

      // Rejected / abandoned → time-limited
      if (STALE_FILTERABLE_STATES.has(app.state)) {
        const lastAction = app.updatedAt ?? app.createdAt;
        return new Date(lastAction) >= oneMonthAgo;
      }

      // Fulfilled + paid → time-limited
      if (
        app.state === 'TAS.ApplicationState.FULFILLED' &&
        PAID_PAYMENT_STATES.has(app.paymentState)
      ) {
        const lastAction = app.updatedAt ?? app.createdAt;
        return new Date(lastAction) >= oneMonthAgo;
      }

      // Everything else (including Fulfilled + not paid) → always show
      return true;
    });

    // Collect IDs of program instances that already have applications
    const instancesWithApps = new Set(
      visibleApps.map((app) => app.programInstanceId).filter((id): id is string => id !== null)
    );

    // Program instances with NO child applications
    const standalonePrograms = this.programs.filter((p) => !instancesWithApps.has(p.id));

    // Build unified list
    const items: Array<
      | { kind: 'application'; data: LiveTasApplication; sortDate: string }
      | { kind: 'program'; data: LiveTasProgram; sortDate: string }
    > = [];

    for (const app of visibleApps) {
      items.push({
        kind: 'application',
        data: app,
        sortDate: app.updatedAt ?? app.createdAt,
      });
    }

    for (const program of standalonePrograms) {
      items.push({
        kind: 'program',
        data: program,
        sortDate: program.updatedAt ?? '',
      });
    }

    // Sort by most recent first
    items.sort((a, b) => (b.sortDate > a.sortDate ? 1 : b.sortDate < a.sortDate ? -1 : 0));

    return items;
  }

  /**
   * Count of items currently in review
   */
  get tasksRequiredCount() {
    return this.inReviewCount;
  }

  /**
   * Calculate progress percentage for the benefit usage
   */
  get benefitUsagePercentage() {
    const limitsStatus = this.args.model.limitsStatus;
    if (!limitsStatus) return 0;

    const usage = limitsStatus.details?.batch?.annual_usage ?? 0;
    const limit = limitsStatus.details?.batch?.annual_limit ?? 0;

    if (limit <= 0) return 0;
    return Math.min(100, Math.round((usage / limit) * 100));
  }

  /**
   * Annual limit (in cents)
   * Uses the highest value across all levels (batch, scheme, product, company)
   */
  get annualLimit() {
    const limitsStatus = this.args.model.limitsStatus;
    if (!limitsStatus) return 0;

    const batchLimit = limitsStatus.details?.batch?.annual_limit ?? 0;
    const schemeLimit = limitsStatus.details?.scheme?.annual_limit ?? 0;
    const productLimit = limitsStatus.details?.product?.annual_limit ?? 0;
    const companyLimit = limitsStatus.details?.company?.annual_limit ?? 0;

    return Math.max(batchLimit, schemeLimit, productLimit, companyLimit);
  }

  /**
   * Net paid amount (in cents)
   * Uses the highest value across all levels (batch, scheme, product, company).
   */
  get paidAmount() {
    const limitsStatus = this.args.model.limitsStatus;
    if (!limitsStatus) return 0;

    const batchUsage = limitsStatus.details?.batch?.annual_usage ?? 0;
    const schemeUsage = limitsStatus.details?.scheme?.annual_usage ?? 0;
    const productUsage = limitsStatus.details?.product?.annual_usage ?? 0;
    const companyUsage = limitsStatus.details?.company?.annual_usage ?? 0;

    return Math.max(batchUsage, schemeUsage, productUsage, companyUsage);
  }

  /**
   * External spend from limits service (in cents)
   * Uses the highest value across all levels (batch, scheme, product, company).
   * Includes root-level external usage when present.
   */
  get externalAmount() {
    const limitsStatus = this.args.model.limitsStatus;
    if (!limitsStatus) return 0;

    const batchExternal = limitsStatus.details?.batch?.external_annual_usage ?? 0;
    const schemeExternal = limitsStatus.details?.scheme?.external_annual_usage ?? 0;
    const productExternal = limitsStatus.details?.product?.external_annual_usage ?? 0;
    const companyExternal = limitsStatus.details?.company?.external_annual_usage ?? 0;
    const rootExternal = limitsStatus.external_annual_usage ?? 0;

    return Math.max(batchExternal, schemeExternal, productExternal, companyExternal, rootExternal);
  }

  /**
   * Total used = paid + external (no pending)
   */
  get totalUsed() {
    return this.paidAmount + this.externalAmount;
  }

  /**
   * Whether there is a limit
   */
  get hasLimit() {
    return this.annualLimit > 0;
  }

  /**
   * Minimum visible percentage
   */
  get minVisiblePercentage() {
    return 5;
  }

  /**
   * Total used percentage (with minimum threshold)
   */
  get totalUsedPercentage() {
    if (!this.hasLimit || this.totalUsed <= 0) return 0;
    const rawPercentage = (this.totalUsed / this.annualLimit) * 100;
    return Math.min(100, Math.max(this.minVisiblePercentage, rawPercentage));
  }

  /**
   * Paid segment percentage
   */
  get paidPercentage() {
    if (!this.hasLimit || this.paidAmount <= 0 || this.totalUsed <= 0) return 0;
    return (this.paidAmount / this.totalUsed) * this.totalUsedPercentage;
  }

  /**
   * External segment percentage
   */
  get externalPercentage() {
    if (!this.hasLimit || this.externalAmount <= 0 || this.totalUsed <= 0) return 0;
    return (this.externalAmount / this.totalUsed) * this.totalUsedPercentage;
  }

  /**
   * Get remaining amount
   */
  get remainingAmount() {
    const limitsStatus = this.args.model.limitsStatus;
    if (!limitsStatus) return 0;

    const available = limitsStatus.available;
    if (available !== null && available !== undefined) {
      return Math.max(0, available);
    }

    if (!this.annualLimit) {
      return 0;
    }
    return Math.max(0, this.annualLimit - this.totalUsed);
  }

  /**
   * Style helpers for progress bar segments
   */
  get paidStyle() {
    return htmlSafe(`width: ${this.paidPercentage}%`);
  }

  get externalStyle() {
    return htmlSafe(`width: ${this.externalPercentage}%`);
  }

  applicationAmountInfo = (application: LiveTasApplication) => {
    const isRequestedState = REQUESTED_APPLICATION_STATES.has(application.state);
    const reimbursementApprovedTotal = application.approvedTotal;
    const reimbursementRequestedTotal = application.requestedTotal;

    const courseCashApprovedTotal = isRequestedState
      ? (application.courseCashPreApprovedTotal ?? application.courseCashFinalApprovedTotal ?? 0)
      : (application.courseCashFinalApprovedTotal ?? application.courseCashPreApprovedTotal ?? 0);

    const courseCashUsed = application.courseCashUsed ?? 0;
    const courseCashReserved = application.courseCashReserved ?? 0;

    // Only consider it a course cash app if there's an approved amount OR actual usage/reserved amounts
    const isCourseCashApp =
      courseCashApprovedTotal > 0 || courseCashUsed > 0 || courseCashReserved > 0;

    // CourseCash display: used > reserved > pre-approved (requested)
    let courseCashAmount = courseCashUsed;
    let courseCashLabelKey = 'tuition_assistance.reimagined.index.application_amount_used';
    if (courseCashUsed <= 0 && courseCashReserved > 0) {
      courseCashAmount = courseCashReserved;
      courseCashLabelKey = 'tuition_assistance.reimagined.index.application_amount_reserved';
    } else if (courseCashUsed <= 0 && courseCashReserved <= 0 && courseCashApprovedTotal > 0) {
      courseCashAmount = courseCashApprovedTotal;
      courseCashLabelKey = 'tuition_assistance.reimagined.index.application_amount_requested';
    }

    if (application.paymentState === 'TAS.ApplicationPaymentState.REFUND_REQUESTED') {
      const amount = application.approvedTotal;
      if (amount <= 0) return null;
      return {
        reimbursementAmount: amount,
        reimbursementLabelKey: 'tuition_assistance.reimagined.index.application_amount_repayment',
        courseCashAmount,
        courseCashLabelKey,
        hasCourseCash: isCourseCashApp,
      };
    }

    if (
      PAID_APPLICATION_STATES.has(application.paymentState) ||
      application.paymentState === 'TAS.ApplicationPaymentState.PAID_IN_PART'
    ) {
      const amount = application.paidTotal || application.approvedTotal;
      if (amount <= 0) return null;
      return {
        reimbursementAmount: amount,
        reimbursementLabelKey: 'tuition_assistance.reimagined.index.application_amount_paid',
        courseCashAmount,
        courseCashLabelKey,
        hasCourseCash: isCourseCashApp,
      };
    }

    if (PENDING_APPLICATION_STATES.has(application.state)) {
      const amount = reimbursementApprovedTotal;
      if (amount <= 0 && reimbursementRequestedTotal <= 0 && !isCourseCashApp) return null;
      return {
        reimbursementAmount: amount > 0 ? amount : reimbursementRequestedTotal,
        reimbursementLabelKey: 'tuition_assistance.reimagined.index.application_amount_requested',
        courseCashAmount,
        courseCashLabelKey,
        hasCourseCash: isCourseCashApp,
      };
    }

    // Fallback: approved > requested
    const reimbursementAmount =
      reimbursementApprovedTotal > 0 ? reimbursementApprovedTotal : reimbursementRequestedTotal;
    if (reimbursementAmount <= 0 && !isCourseCashApp) return null;

    return {
      reimbursementAmount,
      reimbursementLabelKey:
        reimbursementApprovedTotal > 0
          ? 'tuition_assistance.reimagined.index.application_amount_approved'
          : 'tuition_assistance.reimagined.index.application_amount_requested',
      courseCashAmount,
      courseCashLabelKey,
      hasCourseCash: isCourseCashApp,
    };
  };

  // Template helpers for discriminated-union type narrowing on activityItems
  itemProgramName = (item: ActivityItem): string | null =>
    item.kind === 'application' ? item.data.programName : null;

  itemAmountInfo = (item: ActivityItem) =>
    item.kind === 'application' ? this.applicationAmountInfo(item.data) : null;

  itemInstitution = (item: ActivityItem): string | null =>
    item.kind === 'program' ? item.data.institution : null;

  /** Count applications belonging to a given program instance */
  programRequestCount = (programId: string): number =>
    this.args.model.tasData.applications.filter(
      (app) => app.recordType === 'application' && app.programInstanceId === programId
    ).length;

  <template>
    {{pageTitle (t "dashboard")}}
    {{#if this.isEligible}}
      <VStack class="gap-8">
        {{! Conditional hero based on whether there's active activity }}
        {{#if this.hasActiveActivity}}
          {{! Enhanced hero with benefit info when there are active programs/apps }}
          <PageHero
            @gradientClass="bg-gradient-to-br from-violet-50 via-purple-50 to-violet-100"
            @rounded="3xl"
            class="shadow-md border border-violet-200"
          >
            <:default>
              <div class="flex flex-col gap-6">
                <div class="flex items-start justify-between">
                  <div class="max-w-2xl">
                    <div class="flex items-center gap-2 text-sm font-semibold text-violet-700">
                      <PhGraduationCap class="h-4 w-4" />
                      <span>{{this.greetingText}}</span>
                    </div>
                    <Heading @level={{1}} @size="xl" class="text-gray-900">
                      {{t "tuition_assistance.reimagined.dashboard.hero_title"}}
                    </Heading>
                  </div>
                  <LinkTo
                    @route="authenticated.tas.benefit-history"
                    class="text-sm font-semibold text-violet-600 underline hover:text-violet-700 whitespace-nowrap"
                  >
                    {{t "tuition_assistance.reimagined.dashboard.link_view_history"}}
                  </LinkTo>
                </div>

                {{! Benefit usage and activity grid }}
                <div class="grid grid-cols-1 gap-3 lg:grid-cols-2">
                  {{! Left side: Annual Benefits info (from benefit momentum section) }}
                  {{#if @model.limitsStatus}}
                    <div class="rounded-xl bg-white shadow-sm p-4">
                      <div class="flex flex-col gap-3">
                        {{! Remaining Amount Display - different for limited vs unlimited }}
                        <div class="flex flex-col gap-0.5">
                          {{#if this.hasLimit}}
                            <div class="text-2xl font-bold text-gray-900">
                              {{formatCurrencySuperscriptCents this.remainingAmount}}
                            </div>
                            <p class="text-xs text-gray-500">
                              {{t "tas.dashboard.annual_benefits.estimated_remaining_benefit"}}
                            </p>
                          {{else}}
                            <div class="text-2xl font-bold text-gray-900">
                              {{formatCurrencySuperscriptCents this.totalUsed}}
                            </div>
                            <p class="text-xs text-gray-500">
                              {{t "tas.dashboard.annual_benefits.total_used_this_year"}}
                            </p>
                          {{/if}}
                        </div>

                        {{! Progress Bar - only show when there's a limit }}
                        {{#if this.hasLimit}}
                          <div
                            class="w-full h-2 bg-gray-200 rounded-full overflow-hidden flex"
                            role="progressbar"
                            aria-valuenow={{this.benefitUsagePercentage}}
                            aria-valuemin={{0}}
                            aria-valuemax={{100}}
                            aria-label={{t
                              "tas.dashboard.annual_benefits.estimated_remaining_benefit"
                            }}
                          >
                            {{! Paid segment }}
                            {{#if this.paidPercentage}}
                              <div class="h-full bg-violet-500" style={{this.paidStyle}}></div>
                            {{/if}}
                            {{! External segment }}
                            {{#if this.externalPercentage}}
                              <div class="h-full bg-violet-300" style={{this.externalStyle}}></div>
                            {{/if}}
                          </div>
                        {{/if}}

                        {{! Benefit Year Period + Eligible Up To }}
                        <div class="flex items-center justify-between text-xs text-gray-500">
                          <div>
                            {{t "tas.dashboard.annual_benefits.benefit_year"}}
                            <span class="font-medium text-gray-700">
                              {{formatISODateToDisplay
                                @model.limitsStatus.annual_start_date
                                this.intl
                                "2-digit"
                              }}
                              -
                              {{formatISODateToDisplay
                                @model.limitsStatus.annual_end_date
                                this.intl
                                "2-digit"
                              }}
                            </span>
                          </div>
                          {{#if this.hasLimit}}
                            <div>
                              <span class="font-medium">
                                {{t "tas.dashboard.annual_benefits.eligible_up_to"}}
                              </span>
                              <span class="font-semibold text-gray-700">
                                {{formatCurrencySuperscriptCents this.annualLimit}}
                              </span>
                            </div>
                          {{/if}}
                        </div>
                      </div>
                    </div>
                  {{/if}}

                  {{! Right side: Activity metrics }}
                  <div class="grid grid-cols-2 gap-3">
                    <div class="rounded-xl bg-white shadow-sm p-3">
                      <div class="flex flex-col gap-0.5">
                        <div
                          class="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500"
                        >
                          <PhBookOpen class="h-3.5 w-3.5" />
                          {{t "tuition_assistance.reimagined.dashboard.metric_active_requests"}}
                        </div>
                        <Heading @level={{3}} @size="lg" class="text-gray-900">
                          {{this.properActiveCount}}
                        </Heading>
                        <span class="text-xs leading-tight text-gray-500">
                          {{t
                            "tuition_assistance.reimagined.dashboard.metric_active_requests_hint"
                          }}
                        </span>
                      </div>
                    </div>
                    <div class="rounded-xl bg-white shadow-sm p-3">
                      <div class="flex flex-col gap-0.5">
                        <div
                          class="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500"
                        >
                          <PhShieldCheck class="h-3.5 w-3.5" />
                          {{t "tuition_assistance.reimagined.dashboard.metric_tasks_required"}}
                        </div>
                        <Heading @level={{3}} @size="lg" class="text-gray-900">
                          {{this.tasksRequiredCount}}
                        </Heading>
                        <span class="text-xs leading-tight text-gray-500">
                          {{t "tuition_assistance.reimagined.dashboard.metric_tasks_required_hint"}}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </:default>
          </PageHero>
        {{else}}
          {{! Simple hero when no active activity yet (getting started) }}
          <PageHero
            @gradientClass="bg-gradient-to-br from-violet-50 via-purple-50 to-violet-100"
            @rounded="3xl"
            class="shadow-md border border-violet-200"
          >
            <:default>
              <div class="flex items-start justify-between">
                <div class="max-w-2xl">
                  <div class="flex items-center gap-2 text-sm font-semibold text-violet-700">
                    <PhGraduationCap class="h-4 w-4" />
                    <span>{{this.greetingText}}</span>
                  </div>
                  <Heading @level={{1}} @size="xl" class="text-gray-900">
                    {{t "tuition_assistance.reimagined.dashboard.hero_title"}}
                  </Heading>
                  <Text class="text-sm text-gray-600">
                    {{t "tuition_assistance.reimagined.dashboard.hero_subtitle"}}
                  </Text>
                  <div class="mt-4">
                    <a href="#eligible-benefits">
                      <Button @intent="primary">
                        {{t "tuition_assistance.reimagined.dashboard.cta_explore_programs"}}
                      </Button>
                    </a>
                  </div>
                </div>
                <LinkTo
                  @route="authenticated.tas.benefit-history"
                  class="text-sm font-semibold text-violet-600 underline hover:text-violet-700 whitespace-nowrap"
                >
                  {{t "tuition_assistance.reimagined.dashboard.link_view_history"}}
                </LinkTo>
              </div>
            </:default>
          </PageHero>
        {{/if}}

        {{! Activity Section — unified programs + applications }}
        <DataIndicator @source={{this.tasSource}} @showBadge={{true}} @dataSourceId="tas-activity">
          <Section>
            <:header>
              <div class="flex items-center justify-between">
                <Heading @level={{2}} @size="lg">{{t
                    "tuition_assistance.reimagined.dashboard.section_activity"
                  }}</Heading>
                <div class="flex items-center gap-3">
                  {{#if this.isDependentProgramsEnabled}}
                    <LinkTo @route="authenticated.tas.dependent-programs">
                      <Button @intent="primary" @appearance="outlined" @size="sm">
                        {{t "common.tuition_assistance.dependent_programs"}}
                      </Button>
                    </LinkTo>
                  {{/if}}
                  <LinkTo
                    @route="authenticated.tas.benefit-history"
                    class="tio-copy text-sm font-semibold"
                  >
                    {{t "tuition_assistance.reimagined.dashboard.link_view_applications"}}
                  </LinkTo>
                </div>
              </div>
            </:header>
            <:body>
              {{#if this.activityItems.length}}
                <div class="grid grid-cols-1 gap-4">
                  {{#each this.activityItems as |item|}}
                    {{#if (eq item.kind "application")}}
                      <ActivityCard
                        @route="authenticated.tas.applications.show"
                        @model={{item.data.id}}
                        @name={{item.data.name}}
                        @state={{item.data.state}}
                        @shortId={{item.data.id}}
                        @programName={{this.itemProgramName item}}
                        @updatedAt={{item.data.updatedAt}}
                        @amountInfo={{this.itemAmountInfo item}}
                      />
                    {{else}}
                      <ActivityCard
                        @route="authenticated.tas.programs.show"
                        @model={{item.data.id}}
                        @name={{item.data.name}}
                        @state={{item.data.state}}
                        @shortId={{item.data.id}}
                        @iconKind="program"
                        @institution={{this.itemInstitution item}}
                        @updatedAt={{item.data.updatedAt}}
                      >
                        <:metrics>
                          {{#if (canAddRequests item.data.state)}}
                            <div class="hidden shrink-0 md:flex">
                              <div class="rounded-xl bg-gray-100 px-4 py-3 text-center min-w-30">
                                <Text class="text-xs font-medium text-gray-600">
                                  {{t
                                    "tuition_assistance.reimagined.dashboard.activity_requests_label"
                                  }}
                                </Text>
                                <div class="text-lg font-bold text-gray-900">
                                  {{this.programRequestCount item.data.id}}
                                </div>
                                <Text class="text-xs text-gray-500 font-medium">
                                  {{t
                                    "tuition_assistance.reimagined.dashboard.activity_requests_add_now"
                                  }}
                                </Text>
                              </div>
                            </div>
                          {{/if}}
                        </:metrics>
                      </ActivityCard>
                    {{/if}}
                  {{/each}}
                </div>
              {{else}}
                <div class="rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-8">
                  <Heading @level={{3}}>{{t
                      "tuition_assistance.reimagined.dashboard.empty_activity_title"
                    }}</Heading>
                  <Text @variant="muted">
                    {{t "tuition_assistance.reimagined.dashboard.empty_activity_body"}}
                  </Text>
                  <div class="mt-4">
                    <a href="#eligible-benefits">
                      <Button @intent="primary">{{t
                          "tuition_assistance.reimagined.dashboard.cta_explore_programs"
                        }}</Button>
                    </a>
                  </div>
                </div>
              {{/if}}
            </:body>
          </Section>
        </DataIndicator>

        {{! Eligible Benefits Section }}
        {{#if this.eligibleTemplates.length}}
          <DataIndicator
            @source={{this.tasSource}}
            @showBadge={{true}}
            @dataSourceId="tas-eligible-benefits"
          >
            <Section id="eligible-benefits">
              <:header>
                <Heading @level={{2}} @size="lg">{{t "tas.dashboard.eligible_benefits"}}</Heading>
              </:header>
              <:body>
                <div class="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                  {{#each this.eligibleTemplates as |template|}}
                    <div
                      class="rounded-xl ring-1 ring-gray-950/4 bg-white p-4 shadow-sm hover:shadow-md transition-shadow flex flex-col gap-3"
                    >
                      <div class="flex justify-end gap-2 min-h-5">
                        {{#if template.isEnrolled}}
                          <Chip @appearance="faded" @intent="success" @size="sm">
                            {{t "tas.dashboard.chips.enrolled"}}
                          </Chip>
                        {{/if}}
                        {{#if template.isDirectPayProgram}}
                          <Chip @appearance="faded" @intent="primary" @size="sm">
                            {{htmlSafe (t "common.tas.course_cash.default")}}
                          </Chip>
                        {{/if}}
                      </div>
                      <div class="flex flex-col gap-1">
                        <Heading
                          @level={{3}}
                          @size="base"
                          class="font-semibold"
                        >{{template.name}}</Heading>
                        {{#if template.description}}
                          <Text @variant="muted" class="line-clamp-2 text-sm">
                            {{template.description}}
                          </Text>
                        {{/if}}
                      </div>
                      <div>
                        <LinkTo
                          @route="authenticated.tas.getting-started"
                          @model={{template.id}}
                          class="tio-copy text-sm font-semibold"
                        >
                          {{t "tas.dashboard.see_more"}}
                          <PhArrowRight class="ml-1 inline h-3.5 w-3.5" />
                        </LinkTo>
                      </div>
                    </div>
                  {{/each}}
                </div>
              </:body>
            </Section>
          </DataIndicator>
        {{/if}}
      </VStack>
    {{else}}
      <VStack class="gap-8">
        <PageHero
          @gradientClass="bg-gradient-to-br from-gray-400 via-gray-500 to-gray-600"
          @rounded="3xl"
          class="shadow-xl"
        >
          <:default>
            <div class="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
              <div class="max-w-2xl">
                <div class="flex items-center gap-2 text-sm font-semibold">
                  <PhLock class="h-4 w-4" />
                  <span>{{t "tuition_assistance.default"}}</span>
                </div>
                <Heading @level={{1}} class="text-white">
                  {{t "tuition_assistance.reimagined.dashboard.ineligible_title"}}
                </Heading>
                <Text class="text-white/80">
                  {{t "tuition_assistance.reimagined.dashboard.ineligible_body"}}
                </Text>
              </div>
              <div class="flex h-32 w-32 items-center justify-center rounded-full bg-white/10">
                <PhGraduationCap class="h-16 w-16 text-white/40" />
              </div>
            </div>
          </:default>
        </PageHero>

        <Section>
          <:header>
            <Heading @level={{2}}>{{t
                "tuition_assistance.reimagined.dashboard.ineligible_explore_title"
              }}</Heading>
          </:header>
          <:body>
            <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
              <LinkTo @route="authenticated.reimagined.loans" class="block">
                <div
                  class="rounded-2xl ring-1 ring-gray-950/4 bg-white p-6 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div class="flex items-center gap-4">
                    <div
                      class="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-100"
                    >
                      <PhBank class="h-6 w-6 text-emerald-600" />
                    </div>
                    <div>
                      <Heading @level={{3}}>{{t
                          "tuition_assistance.reimagined.dashboard.ineligible_slr_title"
                        }}</Heading>
                      <Text @variant="muted">{{t
                          "tuition_assistance.reimagined.dashboard.ineligible_slr_body"
                        }}</Text>
                    </div>
                    <PhArrowRight class="ml-auto h-5 w-5 text-gray-400" />
                  </div>
                </div>
              </LinkTo>
              <LinkTo @route="authenticated.reimagined.benefits" class="block">
                <div
                  class="rounded-2xl ring-1 ring-gray-950/4 bg-white p-6 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div class="flex items-center gap-4">
                    <div
                      class="flex h-12 w-12 items-center justify-center rounded-xl bg-violet-100"
                    >
                      <PhCompass class="h-6 w-6 text-violet-600" />
                    </div>
                    <div>
                      <Heading @level={{3}}>{{t
                          "tuition_assistance.reimagined.dashboard.ineligible_all_benefits_title"
                        }}</Heading>
                      <Text @variant="muted">{{t
                          "tuition_assistance.reimagined.dashboard.ineligible_all_benefits_body"
                        }}</Text>
                    </div>
                    <PhArrowRight class="ml-auto h-5 w-5 text-gray-400" />
                  </div>
                </div>
              </LinkTo>
            </div>
          </:body>
        </Section>

        <div class="rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-8 text-center">
          <PhGraduationCap class="mx-auto h-12 w-12 text-gray-300" />
          <Heading @level={{3}} class="mt-4">{{t
              "tuition_assistance.reimagined.dashboard.ineligible_interested_title"
            }}</Heading>
          <Text @variant="muted" class="mt-2 max-w-lg mx-auto">
            {{t "tuition_assistance.reimagined.dashboard.ineligible_interested_body"}}
          </Text>
        </div>
      </VStack>
    {{/if}}
  </template>
}
